import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '~/stores';
import { OtaUpdateStatus } from '~/services/ota/types';
import {
  Container,
  Header,
  IconWrapper,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressText,
} from './styles';

/**
 * Download icon for downloading state.
 */
const DownloadIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

/**
 * Spinner icon for installing state.
 */
const SpinnerIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
  </svg>
);

/**
 * Checkmark icon for success state.
 */
const CheckIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

/**
 * Error icon for failed state.
 */
const ErrorIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

export interface UpdateIndicatorProps {
  /** Whether to show the indicator (optional, uses otaStore by default) */
  visible?: boolean;
}

/**
 * Update progress indicator component.
 *
 * Shows OTA update progress in the top-right corner of the screen.
 * Displays different states: downloading, installing, success, and failed.
 *
 * States:
 * - downloading: Progress bar with percentage
 * - installing: Spinner animation
 * - success: Green checkmark (auto-hides after 3s)
 * - failed: Red X with error message and dismiss button
 *
 * The indicator is positioned above the video content and doesn't block playback controls.
 */
const UpdateIndicator: FC<UpdateIndicatorProps> = observer(({ visible }) => {
  const { otaStore } = useStore();
  const [shouldRender, setShouldRender] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (otaStore.updateStatus === OtaUpdateStatus.SUCCESS) {
      setShowSuccess(true);
      const timeout = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      return () => clearTimeout(timeout);
    } else {
      setShowSuccess(false);
    }
  }, [otaStore.updateStatus]);

  // v1.0.134 (Renato 2026-06-02): Auto-hide failed message após 10s.
  //
  // PROBLEMA: dialog "Falha no update" com botão "Dispensar" ficava ETERNAMENTE
  // sobre o conteúdo se OTA falhasse (caso real: TV Tizen sem Partner Cert
  // tentava aplicar bundle 1.0.117, falhava com "Bundle file not found",
  // dialog ficava visível indefinidamente bloqueando UX).
  //
  // FIX: trata FAILED como notificação não-bloqueante. Auto-clear depois de
  // 10s + chama otaStore.clearUpdate() pra que o próximo checkForUpdate
  // possa tentar de novo sem dialog residual.
  useEffect(() => {
    if (otaStore.updateStatus === OtaUpdateStatus.FAILED) {
      setShowFailed(true);
      console.warn(
        '[UpdateIndicator] FAILED detectado — auto-dismiss em 10s. error=',
        otaStore.error,
      );
      const timeout = setTimeout(() => {
        setShowFailed(false);
        otaStore.clearUpdate();
      }, 10_000);
      return () => clearTimeout(timeout);
    } else {
      setShowFailed(false);
    }
  }, [otaStore.updateStatus, otaStore.error, otaStore]);

  // Determine if indicator should be visible
  const isVisible =
    visible !== undefined
      ? visible
      : otaStore.isUpdating || showSuccess || showFailed;

  // Handle mount/unmount animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else {
      // Delay unmount to allow fade-out animation
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isVisible]);

  // Don't render if not needed
  if (!shouldRender && !isVisible) {
    return null;
  }

  // Render different content based on status
  const renderContent = () => {
    switch (otaStore.updateStatus) {
      case OtaUpdateStatus.DOWNLOADING:
        return (
          <>
            <Header>
              <IconWrapper>
                <DownloadIcon />
              </IconWrapper>
              Baixando update
            </Header>
            <ProgressBarContainer>
              <ProgressBarFill $progress={otaStore.downloadProgress} />
            </ProgressBarContainer>
            <ProgressText>
              {Math.round(otaStore.downloadProgress)}%
            </ProgressText>
          </>
        );

      case OtaUpdateStatus.INSTALLING:
        return (
          <Header>
            <IconWrapper $status="installing">
              <SpinnerIcon />
            </IconWrapper>
            Instalando...
          </Header>
        );

      case OtaUpdateStatus.SUCCESS:
        return (
          <Header>
            <IconWrapper $status="success">
              <CheckIcon />
            </IconWrapper>
            Atualizado!
          </Header>
        );

      case OtaUpdateStatus.FAILED:
        // v1.0.134: notificação compacta auto-dismiss (10s). Botão "Dispensar"
        // removido — em digital signage não pode haver ação humana obrigatória
        // pra continuar reproduzindo. Erro completo vai pro console.warn no
        // useEffect acima.
        return (
          <Header>
            <IconWrapper $status="failed">
              <ErrorIcon />
            </IconWrapper>
            Falha no update
          </Header>
        );

      default:
        return null;
    }
  };

  return <Container $visible={isVisible}>{renderContent()}</Container>;
});

export default UpdateIndicator;
